import type { FC } from "react";
import toast from "react-hot-toast";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  IconButton,
  List,
  ListItem,
  MenuItem,
  Stack,
  SvgIcon,
  TextField,
  Typography,
} from "@mui/material";
import { useRouter } from "src/hooks/use-router";
import { paths } from "src/paths";
import { thunks } from "src/thunks/storyline";
import { useDispatch, useSelector } from "src/store";
import { RouterLink } from "src/components/router-link";
import { useTranslation } from "react-i18next";
import { tokens } from "src/locales/tokens";
import { createRoute } from "react-router-url-params";
import LoadingButton from "@mui/lab/LoadingButton";
import React, { useState } from "react";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import { ContentGoalType } from "src/types/content-goal-type";

// Define the Zod schema
const createValidationSchema = (t: any) =>
  z.object({
    name: z
      .string()
      .max(255, t(tokens.general.validators.maxLength255) as string)
      .min(1, t(tokens.general.validators.required) as string),
    description: z
      .string()
      .min(1, t(tokens.general.validators.required) as string),
    contentGoalType: z.nativeEnum(ContentGoalType, {
      errorMap: () => ({
        message: t(tokens.general.validators.required) as string,
      }),
    }),
    categories: z.array(
      z.object({
        name: z
          .string()
          .max(255, t(tokens.general.validators.maxLength255) as string)
          .min(1, t(tokens.general.validators.required) as string),
        description: z
          .string()
          .max(255, t(tokens.general.validators.maxLength255) as string)
          .min(1, t(tokens.general.validators.required) as string),
      })
    ),
  });

// Define the FormValues type using Zod inference
type FormValues = z.infer<ReturnType<typeof createValidationSchema>>;

export const StorylineCreateForm: FC = (props) => {
  const { t } = useTranslation();
  const router = useRouter();
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { strategyId } = useSelector((state) => state.settings);

  // Create the validation schema
  const validationSchema = createValidationSchema(t);

  // Initialize the form
  const {
    control,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<FormValues>({
    defaultValues: {
      name: "",
      description: "",
      contentGoalType: ContentGoalType.None as any, // This will be caught by validation
      categories: [],
    },
    resolver: zodResolver(validationSchema),
  });

  // Setup field array for categories
  const { fields, append, remove } = useFieldArray({
    control,
    name: "categories",
  });

  const onSubmit = async (data: FormValues): Promise<void> => {
    setIsSubmitting(true);
    try {
      const storyline = await dispatch(
        thunks.createStoryline(strategyId!, {
          name: data.name,
          description: data.description,
          contentGoalType: data.contentGoalType,
          categories: data.categories,
        })
      );
      toast.success(t(tokens.storylines.create.form.successMessage));
      router.push(
        createRoute(paths.storylines.details).link({
          storylineId: storyline.id,
        })
      );
    } catch (err) {
      console.error(err);
      toast.error(t(tokens.general.formError));
      setError("root", { message: "Submission failed" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={4}>
        <Card>
          <CardContent>
            <Stack spacing={3}>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    error={!!errors.name}
                    fullWidth
                    helperText={errors.name?.message}
                    label={t(tokens.storylines.create.form.name)}
                  />
                )}
              />

              <Box>
                <Controller
                  name="contentGoalType"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      error={!!errors.contentGoalType}
                      sx={{
                        width: {
                          xs: "100%",
                          sm: "40%",
                          md: "30%",
                        },
                      }}
                      label={t(tokens.storylines.create.form.contentGoal)}
                      helperText={errors.contentGoalType?.message}
                    >
                      <MenuItem
                        key={ContentGoalType.None}
                        value={ContentGoalType.None}
                      >
                        {t(tokens.general.contentGoalTypes.notSelected)}
                      </MenuItem>
                      <MenuItem
                        key={ContentGoalType.Engagement}
                        value={ContentGoalType.Engagement}
                      >
                        {t(tokens.general.contentGoalTypes.engagement)}
                      </MenuItem>
                      <MenuItem
                        key={ContentGoalType.Community}
                        value={ContentGoalType.Community}
                      >
                        {t(tokens.general.contentGoalTypes.community)}
                      </MenuItem>
                      <MenuItem
                        key={ContentGoalType.Selling}
                        value={ContentGoalType.Selling}
                      >
                        {t(tokens.general.contentGoalTypes.selling)}
                      </MenuItem>
                    </TextField>
                  )}
                />
              </Box>

              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    error={!!errors.description}
                    fullWidth
                    multiline
                    rows={5}
                    helperText={errors.description?.message}
                    label={t(tokens.storylines.create.form.description)}
                  />
                )}
              />
            </Stack>
          </CardContent>
        </Card>
        <Card sx={{ display: "flex", flexDirection: "column" }}>
          <CardHeader title={t(tokens.storylines.create.form.categories)} />
          <Divider />
          {fields.length === 0 && (
            <>
              <CardContent>
                <Typography variant="body2">
                  {t(tokens.storylines.create.form.noCategories)}
                </Typography>
              </CardContent>
              <Divider />
            </>
          )}
          <Box sx={{ overflowX: "auto" }}>
            <List disablePadding>
              {fields.map((field, index) => (
                <ListItem
                  key={field.id}
                  divider
                  sx={{ p: 2, width: "100%" }}
                >
                  <Stack
                    spacing={1}
                    direction={{
                      xs: "column",
                      sm: "row",
                    }}
                    alignItems={"center"}
                    sx={{
                      width: "100%",
                    }}
                  >
                    <Stack
                      spacing={1}
                      sx={{ width: "100%" }}
                      direction="column"
                    >
                      <Controller
                        name={`categories.${index}.name`}
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            error={!!errors.categories?.[index]?.name}
                            fullWidth
                            helperText={
                              errors.categories?.[index]?.name?.message
                            }
                            inputProps={{
                              style: { fontWeight: "500" },
                            }}
                            placeholder={t(
                              tokens.storylines.create.form.placeholders.name
                            )}
                          />
                        )}
                      />

                      <Controller
                        name={`categories.${index}.description`}
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            error={!!errors.categories?.[index]?.description}
                            fullWidth
                            helperText={
                              errors.categories?.[index]?.description?.message
                            }
                            placeholder={t(
                              tokens.storylines.create.form.placeholders
                                .description
                            )}
                          />
                        )}
                      />
                    </Stack>
                    <IconButton onClick={() => remove(index)}>
                      <SvgIcon>
                        <DeleteOutlinedIcon color="error" />
                      </SvgIcon>
                    </IconButton>
                  </Stack>
                </ListItem>
              ))}

              <Box sx={{ p: 2 }}>
                <Button
                  onClick={() => append({ name: "", description: "" })}
                  variant="outlined"
                  color="primary"
                >
                  {t(tokens.general.buttons.add)}
                </Button>
              </Box>
            </List>
          </Box>
        </Card>
        <Stack
          alignItems="center"
          direction="row-reverse"
          justifyContent="flex-end"
          spacing={1}
        >
          <Button
            href={paths.storylines.list}
            color="inherit"
            component={RouterLink}
          >
            {t(tokens.general.buttons.cancel)}
          </Button>
          <LoadingButton
            type="submit"
            disabled={isSubmitting}
            loading={isSubmitting}
            variant="contained"
          >
            {t(tokens.general.buttons.create)}
          </LoadingButton>
        </Stack>
      </Stack>
    </form>
  );
};
